import { QUEUE_STORE, allByPrefix, run } from "./db";

/**
 * Writes made with no network, waiting for one (US-33.4, EP-33 decision 5).
 *
 * <p>One queue for the whole application rather than one per screen. US-06.6 had a cupping outbox in
 * `localStorage`; the roasting board needs the same thing, and two queues would mean two banners, two
 * retry loops and two answers to "how many are waiting".
 *
 * <p>Three rules decide its behaviour, and all three come from the same observation — that these are
 * <em>facts about a moment</em>, not intentions:
 *
 * <ol>
 *   <li><strong>In order.</strong> A drop belongs after its start; sending it first gets a 409 that
 *       says nothing useful.</li>
 *   <li><strong>Stop at the first refusal.</strong> If the server says no to the start, everything
 *       behind it is about a bin in a state nobody expects. The person decides: skip it, or throw it
 *       away. Retrying in a loop would turn one confusing minute into a permanent banner.</li>
 *   <li><strong>Keyed by the person who wrote it.</strong> A tablet is shared; another cupper signing
 *       in must not send cards that are not theirs, under their own token.</li>
 * </ol>
 */

/** What kind of write this is; the version travels with it, as `BODY_VERSION` did in US-06.6. */
export type QueueKind = "cupping-score" | "roast-start" | "roast-drop";

/**
 * Bumped when the body of that kind changes shape. An entry of another version cannot be sent — the
 * server would refuse it with a message about a screen the person never saw — so it is dropped when
 * the queue is read, and the one device that was offline across a deploy loses that write. The
 * alternative is a banner nobody can clear.
 */
export const QUEUE_VERSIONS: Record<QueueKind, number> = {
  "cupping-score": 2,
  "roast-start": 1,
  "roast-drop": 1,
};

export interface QueueEntry {
  /** Stable per subject, so pressing save twice on one card queues one card (`sub|kind|subject`). */
  id: string;
  userSub: string;
  kind: QueueKind;
  method: "POST" | "PUT" | "PATCH";
  /** Path with query, relative to the API origin. */
  url: string;
  body: unknown;
  /** What this is, in the person's own words: "Guji · próbka 3", "RB-2026-0042". */
  label: string;
  createdAt: number;
  attempts: number;
  /** What the server said, in its own words — the fallback when the code has no translation. */
  lastError?: string;
  /**
   * The refusal as the server sent it (RFC 9457), kept whole so the screen can translate it.
   *
   * <p>The sentence in `lastError` is the one the backend wrote for its log, in English. Keeping the
   * problem document means the sheet runs it through `useApiErrorMessage` like every other error in
   * this application, and a Polish cupper reads Polish wherever the code has an entry (CLAUDE.md §7).
   */
  lastProblem?: unknown;
  version: number;
}

const CHANGED_EVENT = "roastery:offline-queue";

export function subscribeToQueue(listener: () => void): () => void {
  window.addEventListener(CHANGED_EVENT, listener);
  return () => window.removeEventListener(CHANGED_EVENT, listener);
}

function announce() {
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

export function entryId(sub: string, kind: QueueKind, subject: string): string {
  return `${sub}|${kind}|${subject}`;
}

/**
 * Everything this user has waiting, oldest first — and only what this version can still send.
 *
 * <p>The prefix is the user: `allByPrefix` over `sub|` is the same trick snapshots use, and it is why
 * a second person signing in sees "2 saves belonging to somebody else" rather than somebody else's
 * work going out under their name.
 */
export async function queued(sub: string): Promise<QueueEntry[]> {
  const rows = await allByPrefix<QueueEntry>(QUEUE_STORE, `${sub}|`);
  return rows
    .filter((row) => row.version === QUEUE_VERSIONS[row.kind])
    .sort((left, right) => left.createdAt - right.createdAt);
}

/** How many entries are waiting that belong to somebody else — the tablet's other cupper. */
export async function queuedByOthers(sub: string): Promise<number> {
  const rows = await allByPrefix<QueueEntry>(QUEUE_STORE, "");
  return rows.filter((row) => row.userSub !== sub).length;
}

export async function enqueue(
  entry: Omit<QueueEntry, "id" | "createdAt" | "attempts" | "version"> & { subject: string },
): Promise<void> {
  const { subject, ...rest } = entry;
  const row: QueueEntry = {
    ...rest,
    id: entryId(entry.userSub, entry.kind, subject),
    createdAt: Date.now(),
    attempts: 0,
    version: QUEUE_VERSIONS[entry.kind],
  };
  try {
    await run(QUEUE_STORE, "readwrite", (store) => store.put(row));
    announce();
  } catch {
    // Nothing written and nothing to say here; the caller reports the failure to the person.
  }
}

export async function remove(id: string): Promise<void> {
  try {
    await run(QUEUE_STORE, "readwrite", (store) => store.delete(id));
    announce();
  } catch {
    // As above.
  }
}

export async function removeAllOf(sub: string): Promise<void> {
  const rows = await allByPrefix<QueueEntry>(QUEUE_STORE, `${sub}|`);
  await Promise.all(rows.map((row) => remove(row.id)));
}

/** Everything queued by anybody but this user — what "Delete" on the other person's entries clears. */
export async function removeEverybodyElse(sub: string): Promise<void> {
  const rows = await allByPrefix<QueueEntry>(QUEUE_STORE, "");
  await Promise.all(rows.filter((row) => row.userSub !== sub).map((row) => remove(row.id)));
}

async function markFailed(entry: QueueEntry, message: string, problem?: unknown): Promise<void> {
  try {
    await run(QUEUE_STORE, "readwrite", (store) =>
      store.put({ ...entry, attempts: entry.attempts + 1, lastError: message, lastProblem: problem }),
    );
    announce();
  } catch {
    // The message is on screen either way; this only survives a reload.
  }
}

export interface FlushResult {
  sent: number;
  /** The entry the queue stopped on, if it stopped: the person has to decide what to do with it. */
  blocked?: QueueEntry;
}

/**
 * Sends what is waiting, in order, stopping at the first refusal.
 *
 * <p>`send` is the application's authenticated client: the queue knows nothing about tokens, and that
 * matters — an entry written offline goes out with whatever token exists <em>now</em>, which is why
 * the queue is keyed by user in the first place.
 *
 * <p>A failure that is the network (`TypeError` from `fetch`, no status) leaves the entry alone and
 * stops: there is nothing to decide, the network is simply still down. A failure that is an answer
 * (4xx) is recorded on the entry and stops the queue with it named.
 */
export async function flush(
  sub: string,
  send: (entry: QueueEntry) => Promise<{ ok: boolean; status?: number; message?: string; problem?: unknown }>,
): Promise<FlushResult> {
  const waiting = await queued(sub);
  let sent = 0;
  for (const entry of waiting) {
    let answer: { ok: boolean; status?: number; message?: string; problem?: unknown };
    try {
      answer = await send(entry);
    } catch (failure) {
      answer = { ok: false, message: failure instanceof Error ? failure.message : String(failure) };
    }
    if (answer.ok) {
      await remove(entry.id);
      sent += 1;
      continue;
    }
    if (answer.status === undefined) {
      // No answer at all: still offline. Nothing to report and nothing to decide.
      return { sent };
    }
    const message = answer.message ?? `HTTP ${answer.status}`;
    await markFailed(entry, message, answer.problem);
    return { sent, blocked: { ...entry, lastError: message, lastProblem: answer.problem } };
  }
  return { sent };
}

const LAST_SYNC_KEY = "roastery.offline.last-sync";

export function rememberSync(): void {
  try {
    window.localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
  } catch {
    // Then the indicator says "never", which is honest on a device that remembers nothing.
  }
}

export function lastSyncAt(): number | undefined {
  try {
    const raw = window.localStorage.getItem(LAST_SYNC_KEY);
    return raw ? Number(raw) : undefined;
  } catch {
    return undefined;
  }
}
